/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { TChannelsType, TChannel } from "@tivocorp/data-domain-lib";

import { Log } from "../../model/logging";
import { Locale, LocaleKeys } from "../../model/locale";
import { PlaybackContentType } from "../../model/videoplayer/enums";
import { VideoPlaybackController } from "../../model/videoplayer/VideoPlaybackController";
import {
  DEFAULT_ELIGIBILITY,
  DEFAULT_TRICKPLAY_CONFIG
} from "../../model/videoplayer/trickplay";
import { ChannelsInteractor } from "../../model/interactor/channelsInteractor";
import { showToastMessage } from "../toastMessage";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";

const TAG: string = "channelChangeSlice";

/**
 * Debounce delay (ms) for fast channel zapping.
 * First press fires immediately; subsequent rapid presses within this window
 * are accumulated and resolved with a single service call.
 */
const DEBOUNCE_DELAY: number = 800;

// ── Module-level state for debounce (not in Redux — timers are non-serializable) ──

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let fastZapTimer: ReturnType<typeof setTimeout> | null = null;
let isUserFastZapping: boolean = false;

/**
 * The channel a zap burst started from, and the net presses since. Every tune
 * in the burst is resolved from this base rather than from whatever is
 * playing, so a press is not lost when it lands before the previous press's
 * tune has. Null when no burst is in progress.
 */
let burstBaseChannelNumber: string | null = null;
let burstSteps: number = 0;

/**
 * Set once a press in the burst stopped playback. A burst that then settles
 * on no new channel — the presses cancelled out, the target is not tunable,
 * or the lookup failed — has to put playback back, or the screen stays black.
 */
let isPlaybackStoppedForBurst: boolean = false;

/**
 * Advanced by every press. A channel lookup that resolves under an older
 * generation was overtaken by a later press and is dropped, so a slow lookup
 * cannot start playback after the user has moved on.
 */
let zapGeneration: number = 0;

/**
 * Generation of the channel lookup in flight, or null. A press while one is
 * in flight joins its burst: the channel that press is relative to has not
 * been decided yet.
 */
let lookupGeneration: number | null = null;

// ── Slice State ──

export interface ChannelChangeState {
  isChanging: boolean;
  error: string;
}

const INITIAL_STATE: ChannelChangeState = {
  isChanging: false,
  error: ""
};

// ── Slice ──

const channelChangeSlice = createSlice({
  name: "channelChange",
  initialState: INITIAL_STATE,
  reducers: {
    channelChangeStarted(state: ChannelChangeState): void {
      state.isChanging = true;
      state.error = "";
    },
    channelChangeCompleted(state: ChannelChangeState): void {
      state.isChanging = false;
    },
    channelChangeFailed(
      state: ChannelChangeState,
      action: PayloadAction<string>
    ): void {
      state.isChanging = false;
      state.error = action.payload;
    },
    channelChangeReset(state: ChannelChangeState): void {
      state.isChanging = false;
      state.error = "";
    }
  }
});

export const {
  channelChangeStarted,
  channelChangeCompleted,
  channelChangeFailed,
  channelChangeReset
} = channelChangeSlice.actions;

export const channelChangeReducer = channelChangeSlice.reducer;

// ── Selectors ──

export const selectIsChannelChanging = (state: UnityRootState): boolean =>
  state.channelChange.isChanging;

export const selectChannelChangeError = (state: UnityRootState): string =>
  state.channelChange.error;

// ── Thunks ──

/** Ends the burst: the next press starts a new one from what is playing. */
const endBurst = (): void => {
  burstBaseChannelNumber = null;
  burstSteps = 0;
  isPlaybackStoppedForBurst = false;
  lookupGeneration = null;
};

/**
 * Resolves the channel a burst settles on: `steps` channels away from where
 * it started. Null means the presses cancelled out and the channel the burst
 * started from is still the one loaded.
 */
const resolveBurstTarget = async (
  interactor: ChannelsInteractor,
  baseChannelNumber: string,
  steps: number,
  channelsType: TChannelsType
): Promise<TChannel | null> => {
  if (steps > 0) {
    return interactor.getNextChannel(baseChannelNumber, channelsType, steps);
  }
  if (steps < 0) {
    return interactor.getPreviousChannel(
      baseChannelNumber,
      channelsType,
      -steps
    );
  }
  // The burst's first press may already have tuned away from the base, in
  // which case returning to it is a tune of its own.
  const currentChannelNumber: string =
    VideoPlaybackController.getInstance().getCurrentChannelNumber();
  if (currentChannelNumber === baseChannelNumber) {
    return null;
  }
  return interactor.tuneToChannel(baseChannelNumber);
};

/**
 * Executes the channel change service call and starts playback.
 * Called either immediately (single press) or after debounce (fast zap).
 */
const executeChannelChange =
  (channelsType: TChannelsType) =>
  async (dispatch: UnityAppDispatch): Promise<void> => {
    const baseChannelNumber: string | null = burstBaseChannelNumber;
    if (baseChannelNumber === null) {
      return;
    }
    const steps: number = burstSteps;
    const generation: number = zapGeneration;
    lookupGeneration = generation;

    dispatch(channelChangeStarted());

    const direction = steps >= 0 ? "Up" : "Down";
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();

    let channel: TChannel | null;
    try {
      channel = await resolveBurstTarget(
        new ChannelsInteractor(),
        baseChannelNumber,
        steps,
        channelsType
      );
    } catch (error: unknown) {
      if (generation !== zapGeneration) {
        return;
      }
      const shouldRestore: boolean = isPlaybackStoppedForBurst;
      endBurst();
      const message =
        error instanceof Error ? error.message : "Channel change failed";
      Log.e(TAG, "Channel " + direction + " — failed", error);
      dispatch(channelChangeFailed(message));
      if (shouldRestore) {
        vpc.restorePlayback();
      }
      return;
    }

    // A later press has extended the burst since this lookup started; the
    // tune it schedules covers this press too.
    if (generation !== zapGeneration) {
      Log.d(TAG, "Channel " + direction + " — superseded by a later press");
      return;
    }
    const shouldRestore: boolean = isPlaybackStoppedForBurst;
    endBurst();

    if (channel === null) {
      Log.d(TAG, "Channel presses cancelled out — staying on channel");
      if (shouldRestore) {
        vpc.restorePlayback();
      }
      dispatch(channelChangeCompleted());
      return;
    }

    // Not tunable by this app (e.g. AppLinear) — playback stays on the
    // current channel and the press is dropped without a message: an
    // untunable channel is an ordinary gap in the lineup, not a failure
    // the user caused.
    if (!ChannelsInteractor.isTunableChannel(channel)) {
      Log.d(
        TAG,
        "Channel " +
          direction +
          " — channel " +
          String(channel.channelNumber) +
          " is not tunable, ignoring"
      );
      if (shouldRestore) {
        vpc.restorePlayback();
      }
      dispatch(channelChangeCompleted());
      return;
    }

    Log.d(
      TAG,
      "Channel " +
        direction +
        " — tuning to channel: " +
        String(channel.channelNumber)
    );

    try {
      // End any in-progress trick-play session before tuning — the channel
      // is about to be fully replaced, so this is a clean stop (no seek/play)
      // rather than a cancel/confirm that would do wasted seek work.
      if (vpc.isTrickPlayActive()) {
        vpc.trickPlayStop();
      }

      vpc.startPlayback({
        channel: channel,
        playbackContentType: PlaybackContentType.LIVE
      });

      // Re-initialize trick-play after channel change — startPlayback()
      // calls stop() which destroys the previous TrickPlayManager.
      vpc.initTrickPlay(DEFAULT_TRICKPLAY_CONFIG, DEFAULT_ELIGIBILITY);

      dispatch(channelChangeCompleted());
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Channel change failed";
      Log.e(TAG, "Channel " + direction + " — failed", error);
      dispatch(channelChangeFailed(message));
    }
  };

/**
 * Thunk: handles a channel change key press with fast-zap debounce.
 *
 * @param step - +1 for channel up, -1 for channel down
 * @param channelsType - Which channel list to navigate (all, subscribed, etc.)
 */
export const channelChange =
  (step: number, channelsType: TChannelsType) =>
  (dispatch: UnityAppDispatch, _getState: () => UnityRootState): void => {
    const vpc = VideoPlaybackController.getInstance();
    const isLivePlayback: boolean = vpc.isLive();
    const isSocuPlayback: boolean = vpc.isSocu();

    if (!isLivePlayback && !isSocuPlayback) {
      Log.d(TAG, "channelChange — not on live or SoCu content, ignoring");
      dispatch(
        showToastMessage(
          Locale.getString(LocaleKeys.PLAYBACK_CHANNEL_CHANGE_LIVE_ONLY_MESSAGE)
        )
      );
      return;
    }

    const currentChannelNumber = vpc.getCurrentChannelNumber();

    if (!currentChannelNumber) {
      Log.d(TAG, "channelChange — no current channel number, ignoring");
      return;
    }

    // Clear any pending debounce
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }

    zapGeneration += 1;

    // A press while a lookup is still in flight continues that burst rather
    // than starting a new one from what is playing: the lookup has not yet
    // decided the channel this press is relative to.
    const isBurstActive: boolean =
      isUserFastZapping || lookupGeneration !== null;

    if (burstBaseChannelNumber === null) {
      burstBaseChannelNumber = currentChannelNumber;
      burstSteps = 0;
    }
    burstSteps += step;

    if (isBurstActive) {
      // User is zapping fast — stop playback and debounce
      vpc.stop();
      isPlaybackStoppedForBurst = true;
      Log.d(TAG, "Playback stopped — user is fast zapping");

      if (fastZapTimer !== null) {
        clearTimeout(fastZapTimer);
        fastZapTimer = null;
      }

      debounceTimer = setTimeout((): void => {
        Log.d(TAG, "Debounce fired — burstSteps = " + String(burstSteps));
        // The burst ends with the tune it settles on. fastZapTimer was
        // cleared above and is not re-armed, so without this every later
        // press — however far apart — stopped playback and waited out the
        // debounce, leaving a black screen and a window for the stopped
        // channel's session response to land in.
        isUserFastZapping = false;
        debounceTimer = null;
        dispatch(executeChannelChange(channelsType));
      }, DEBOUNCE_DELAY);
    } else {
      // First press — act immediately
      Log.d(TAG, "Single press — burstSteps = " + String(burstSteps));
      dispatch(executeChannelChange(channelsType));

      // Mark as fast-zapping so subsequent presses within the window debounce
      isUserFastZapping = true;
      fastZapTimer = setTimeout((): void => {
        isUserFastZapping = false;
      }, DEBOUNCE_DELAY);
    }
  };

/**
 * Thunk: channel up — convenience wrapper.
 */
export const channelUp =
  (channelsType: TChannelsType) =>
  (dispatch: UnityAppDispatch): void => {
    dispatch(channelChange(1, channelsType));
  };

/**
 * Thunk: channel down — convenience wrapper.
 */
export const channelDown =
  (channelsType: TChannelsType) =>
  (dispatch: UnityAppDispatch): void => {
    dispatch(channelChange(-1, channelsType));
  };

/**
 * Clears all debounce timers and resets internal state.
 * Call on component unmount or navigation away from playback.
 */
export const clearChannelChangeTimers = (): void => {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (fastZapTimer !== null) {
    clearTimeout(fastZapTimer);
    fastZapTimer = null;
  }
  // Also drops any lookup still in flight: it belongs to a player that is
  // being left.
  zapGeneration += 1;
  endBurst();
  isUserFastZapping = false;
  Log.d(TAG, "Channel change timers cleared");
};
